
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4cqjdqels5gugpjmge0', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 overflow-hidden">
      {/* Dark background with subtle texture */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] via-[#0f0f0f] to-black"></div>
      
      {/* Ambient light effect */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F6B766]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#F4A259]/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-light text-white mb-6">
            Let's Talk About <span className="text-[#F6B766]">Your Project</span>
          </h2>
          <p className="text-xl text-white/60 max-w-3xl mx-auto font-light leading-relaxed mb-12">
            당신의 게임이 전세계로 퍼져나갈 준비가 되셨나요?
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Contact Information */}
          <div className="space-y-10">
            <div>
              <h3 className="text-3xl font-light text-white mb-8 border-b border-[#F6B766]/20 pb-4">
                Get In Touch
              </h3>
              <div className="space-y-8">
                <div className="flex items-start group">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F6B766]/10 rounded-lg group-hover:bg-[#F6B766]/20 transition-colors">
                    <i className="ri-map-pin-line text-[#F6B766] text-2xl"></i>
                  </div>
                  <div className="ml-6">
                    <h4 className="font-medium text-white/90 mb-2 text-lg">Location</h4>
                    <p className="text-white/60 leading-relaxed">Seoul, South Korea</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F6B766]/10 rounded-lg group-hover:bg-[#F6B766]/20 transition-colors">
                    <i className="ri-mail-line text-[#F6B766] text-2xl"></i>
                  </div>
                  <div className="ml-6">
                    <h4 className="font-medium text-white/90 mb-2 text-lg">Email</h4>
                    <p className="text-white/60">haddon@mondaypawn.com</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F6B766]/10 rounded-lg group-hover:bg-[#F6B766]/20 transition-colors">
                    <i className="ri-linkedin-box-fill text-[#F6B766] text-2xl"></i>
                  </div>
                  <div className="ml-6">
                    <h4 className="font-medium text-white/90 mb-2 text-lg">LinkedIn</h4>
                    <a 
                      href="https://www.linkedin.com/in/haddon614493275" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white/60 hover:text-[#F6B766] transition-colors cursor-pointer"
                    >
                      linkedin.com/in/haddon614493275
                    </a>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F6B766]/10 rounded-lg group-hover:bg-[#F6B766]/20 transition-colors">
                    <i className="ri-user-line text-[#F6B766] text-2xl"></i>
                  </div>
                  <div className="ml-6">
                    <h4 className="font-medium text-white/90 mb-2 text-lg">Founder</h4>
                    <p className="text-white/60">Bohyun Kim</p>
                    <p className="text-white/40 text-sm mt-1">Creative Director</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative quote */}
            <div className="mt-12 p-8 bg-gradient-to-br from-[#F6B766]/5 to-transparent border-l-4 border-[#F6B766] rounded-r-lg">
              <p className="text-white/70 italic text-lg leading-relaxed">
                "Every great game deserves a trailer that captures its soul. Let's find yours."
              </p>
              <p className="text-[#F6B766] mt-4 font-medium">— MondayPawn Team</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-10 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-7" data-readdy-form>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-3">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoComplete="name"
                  className="w-full px-5 py-4 bg-black/30 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#F6B766] focus:border-transparent text-white placeholder-white/30 transition-all text-base"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-3">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  autoComplete="email"
                  className="w-full px-5 py-4 bg-black/30 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#F6B766] focus:border-transparent text-white placeholder-white/30 transition-all text-base"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-3">
                  Tell Us About Your Project
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-5 py-4 bg-black/30 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#F6B766] focus:border-transparent resize-none text-white placeholder-white/30 transition-all text-base"
                  placeholder="What kind of trailer or ad are you looking for?"
                />
                <p className="text-sm text-white/40 mt-2">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#F6B766] to-[#F4A259] text-black py-5 px-8 rounded-xl hover:shadow-lg hover:shadow-[#F6B766]/20 transition-all font-semibold text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="p-5 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <p className="text-green-400 text-center">✓ Message sent! We'll get back to you soon.</p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="p-5 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-red-400 text-center">✗ Something went wrong. Please try again.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
